// src/portal-app/shell/kit/kitCss.js
//
// ONE stylesheet for the whole portal-app kit, injected once. Keeping every
// kit class in a single sheet is deliberate: it is the only way to guarantee
// the kit's components share exactly one spacing/type/colour rhythm. Includes
// the .opk-* rules for SegmentedControl, SearchInput, StatusBadge and
// ConfirmSheet, moved here from operations/kit.js (Plan 4 Task 1) — kept
// byte-identical class names since 19 files already depend on them.
//
// Tokens only (var(--c-*)); translucency via color-mix. rgba() appears only
// in depth shadows — the approved exception, matching LiquidGlass.js.
// Logical properties only (margin-inline-*, inset-inline-*) for RTL safety.
const CSS = `
/* ---- shared primitives ---- */
.pk-num{font-variant-numeric:tabular-nums;}
.pk-label{font-size:11px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;
  color:var(--c-textLight);}
.pk-section-label{font-size:11px;font-weight:700;letter-spacing:.8px;
  text-transform:uppercase;color:var(--c-textLight);
  padding-inline:16px;margin-block:18px 8px;}
.pk-chev{flex:none;color:var(--c-mutedLight);display:inline-flex;}
/* Points "forward"; forward is reversed under RTL. Same recipe as
   PortalLogin.js's .plg-arrow, GlassNavBar's back chevron and HubScreen's
   card chevron — the one approved physical-transform exception. */
[dir="rtl"] .pk-chev{transform:scaleX(-1);}

/* ---- status pill ---- */
.pk-pill{display:inline-flex;align-items:center;gap:4px;
  padding-block:3px;padding-inline:8px;border-radius:999px;
  font-size:9.5px;font-weight:700;letter-spacing:.4px;text-transform:uppercase;
  white-space:nowrap;font-variant-numeric:tabular-nums;}
.pk-pill-green{background:color-mix(in srgb,var(--c-success) 12%,transparent);color:var(--c-success);}
.pk-pill-red{background:color-mix(in srgb,var(--c-red) 11%,transparent);color:var(--c-red);}
.pk-pill-amber{background:color-mix(in srgb,var(--c-amber) 13%,transparent);color:var(--c-amber);}
.pk-pill-blue{background:color-mix(in srgb,var(--c-blue) 11%,transparent);color:var(--c-blue);}
.pk-pill-teal{background:color-mix(in srgb,var(--c-teal) 12%,transparent);color:var(--c-teal);}
.pk-pill-purple{background:color-mix(in srgb,var(--c-purple) 12%,transparent);color:var(--c-purple);}
.pk-pill-muted{background:color-mix(in srgb,var(--c-ink) 7%,transparent);color:var(--c-muted);}

/* ---- icon square ---- */
.pk-isq{flex:none;display:inline-flex;align-items:center;justify-content:center;
  border-radius:9px;}

/* ---- CondensedRow ---- */
/* Row 1 carries name + value; row 2 carries secondary + pill and indents
   44px (32 icon + 12 gap) so it aligns under the NAME, not the icon.
   Secondary text never wraps — that wrap is exactly what made the old
   cards 110px tall (see spec §1, complaint 3). */
/* NB: no background/border reset here. This class lands on a GlassCard, whose
   .lqg-card rule supplies both. Resetting them would race the two injected
   stylesheets and could strip the glass surface depending on injection order. */
.pk-row{display:block;width:100%;box-sizing:border-box;
  padding-block:14px;padding-inline:16px;text-align:start;
  font-family:inherit;transition:transform .12s ease;}
.pk-row-tappable{cursor:pointer;}
.pk-row-tappable:active{transform:scale(.97);}
.pk-row-top{display:flex;align-items:center;gap:12px;}
.pk-row-name{font-size:15px;font-weight:800;color:var(--c-ink);
  letter-spacing:-.1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.pk-row-spacer{flex:1 1 auto;min-width:8px;}
.pk-row-value{font-size:22px;font-weight:800;color:var(--c-ink);
  letter-spacing:-.4px;font-variant-numeric:tabular-nums;white-space:nowrap;}
.pk-row-bottom{display:flex;align-items:center;gap:8px;
  margin-inline-start:44px;margin-block-start:5px;}
.pk-row-sec{font-size:11.5px;font-weight:500;color:var(--c-textLight);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;}

/* ---- HeroStat ---- */
.pk-hero{display:flex;flex-direction:column;gap:6px;}
.pk-hero-value{font-size:38px;font-weight:800;color:var(--c-ink);
  letter-spacing:-1.2px;line-height:1.05;}
.pk-hero-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.pk-hero-caption{font-size:12.5px;font-weight:500;color:var(--c-textLight);}
.pk-hero-spark{margin-block-start:10px;}
.pk-delta{display:inline-flex;align-items:center;gap:3px;
  padding-block:3px;padding-inline:8px;border-radius:999px;
  font-size:11.5px;font-weight:700;font-variant-numeric:tabular-nums;}
.pk-delta-up{background:color-mix(in srgb,var(--c-success) 12%,transparent);color:var(--c-success);}
.pk-delta-down{background:color-mix(in srgb,var(--c-red) 11%,transparent);color:var(--c-red);}
.pk-delta-flat{background:color-mix(in srgb,var(--c-ink) 7%,transparent);color:var(--c-muted);}

/* ---- AvatarRow ---- */
.pk-av-row{display:flex;align-items:center;}
.pk-av{flex:none;display:inline-flex;align-items:center;justify-content:center;
  border-radius:50%;color:var(--c-white);font-weight:800;
  box-shadow:0 0 0 2px var(--c-surface);}
.pk-av + .pk-av{margin-inline-start:-9px;}
.pk-av-more{background:color-mix(in srgb,var(--c-ink) 45%,transparent);}

/* ---- LeanHero ---- */
/* Capped at ~120px by construction: one label, one figure line, one avatar
   row. No body slot exists, so it cannot grow. */
.pk-lean{padding:16px;}
.pk-lean-top{display:flex;align-items:flex-start;gap:12px;}
.pk-lean-main{flex:1 1 auto;min-width:0;}
.pk-lean-figure{display:flex;align-items:baseline;gap:8px;margin-block-start:4px;}
.pk-lean-value{font-size:32px;font-weight:800;color:var(--c-ink);
  letter-spacing:-1px;line-height:1.1;}
.pk-lean-suffix{font-size:13px;font-weight:600;color:var(--c-textLight);}
.pk-lean-avatars{margin-block-start:12px;}

/* ---- VerdictCard ---- */
.pk-verdict{padding:18px 16px;}
.pk-verdict-sentence{margin:8px 0 0;font-size:17px;font-weight:700;
  color:var(--c-ink);line-height:1.42;letter-spacing:-.2px;}
.pk-verdict-stats{display:flex;gap:28px;margin-block-start:14px;flex-wrap:wrap;}
.pk-verdict-stat{min-width:0;}
.pk-verdict-stat-value{font-size:24px;font-weight:800;color:var(--c-ink);
  letter-spacing:-.6px;font-variant-numeric:tabular-nums;line-height:1.15;}
.pk-verdict-stat-caption{font-size:11.5px;font-weight:500;
  color:var(--c-textLight);margin-block-start:2px;}
.pk-verdict-spark{margin-block-start:14px;}

/* ---- AttentionList ---- */
.pk-att-list{display:flex;flex-direction:column;gap:8px;padding-inline:16px;}
/* As with .pk-row: no background/border reset — GlassCard's .lqg-card owns both. */
.pk-att-row{display:flex;align-items:center;gap:10px;width:100%;
  box-sizing:border-box;min-height:52px;padding-block:10px;padding-inline:14px;
  text-align:start;font-family:inherit;transition:transform .12s ease;}
.pk-att-tappable{cursor:pointer;}
.pk-att-tappable:active{transform:scale(.97);}
.pk-att-dot{flex:none;width:8px;height:8px;border-radius:50%;}
.pk-att-dot-red{background:var(--c-red);}
.pk-att-dot-amber{background:var(--c-amber);}
.pk-att-dot-green{background:var(--c-success);}
.pk-att-dot-blue{background:var(--c-blue);}
.pk-att-dot-teal{background:var(--c-teal);}
.pk-att-dot-purple{background:var(--c-purple);}
.pk-att-dot-muted{background:var(--c-mutedLight);}
.pk-att-text{flex:1 1 auto;min-width:0;font-size:13.5px;font-weight:600;
  color:var(--c-ink);line-height:1.35;}

/* ---- ShareBar ---- */
.pk-share{display:flex;flex-direction:column;gap:10px;}
.pk-share-track{display:flex;width:100%;height:10px;border-radius:999px;
  overflow:hidden;background:color-mix(in srgb,var(--c-ink) 8%,transparent);}
.pk-share-seg{display:block;height:100%;}
.pk-share-leg{display:flex;flex-wrap:wrap;gap:6px 18px;}
.pk-share-leg-item{display:inline-flex;align-items:center;gap:6px;
  font-size:11.5px;font-weight:500;color:var(--c-textLight);}
.pk-share-dot{flex:none;width:8px;height:8px;border-radius:50%;}
.pk-share-leg-value{font-weight:700;color:var(--c-ink);
  font-variant-numeric:tabular-nums;}

@media (prefers-reduced-motion: reduce){
  .pk-att-row,.pk-att-tappable:active{transition:none;transform:none;}
}

/* ---- TabPills ---- */
/* Scrolls horizontally; pills size to their own text. The approved
   horizontal-scroll exception (like RangeBar), never squeezed segments. */
/* 4px matches the ::after overlay's inset-block:-4px so the extended hit area
   sits inside .pk-tabs' clip box. overflow-x:auto forces overflow-y to compute
   as auto, so anything beyond the padding box is clipped and untappable. */
.pk-tabs{display:flex;gap:8px;overflow-x:auto;padding-inline:16px;
  padding-block:4px;-webkit-overflow-scrolling:touch;scrollbar-width:none;}
.pk-tabs::-webkit-scrollbar{display:none;}
.pk-tab{flex:none;min-height:36px;padding-inline:16px;border-radius:999px;
  font-family:inherit;font-size:13px;font-weight:600;white-space:nowrap;
  cursor:pointer;color:var(--c-textLight);
  background:color-mix(in srgb,var(--c-surface) 38%,transparent);
  border:1px solid color-mix(in srgb,var(--c-surface) 55%,transparent);
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);
  transition:transform .12s ease,background .15s ease,color .15s ease;position:relative;}
.pk-tab::after{content:'';position:absolute;inset-block:-4px;inset-inline:0;}
.pk-tab:active{transform:scale(.96);}
.pk-tab-active{background:var(--c-green);border-color:var(--c-green);
  color:var(--c-white);font-weight:700;}

@media (prefers-reduced-motion: reduce){
  .pk-tab,.pk-tab:active{transition:none;transform:none;}
}

/* ---- GroupedRows ---- */
.pk-grp{overflow:hidden;}
.pk-grp-row{display:flex;align-items:center;gap:10px;width:100%;
  box-sizing:border-box;min-height:52px;padding-inline:16px;padding-block:8px;
  text-align:start;font-family:inherit;border:none;background:transparent;}
.pk-grp-divided{box-shadow:inset 0 -1px 0 color-mix(in srgb,var(--c-ink) 7%,transparent);}
.pk-grp-tappable{cursor:pointer;transition:background .15s ease;}
.pk-grp-tappable:active{background:color-mix(in srgb,var(--c-ink) 4%,transparent);}
.pk-grp-icon{flex:none;display:inline-flex;color:var(--c-muted);}
.pk-grp-label{font-size:13px;font-weight:600;color:var(--c-textLight);
  white-space:nowrap;}
/* Values wrap rather than truncate (spec §9 default) — a detail screen's
   only job is showing field values, and truncating the longest ones (e.g.
   an email address) with no tooltip and no way to read them defeats that.
   §6's nowrap requirement is scoped to CondensedRow's secondary text only;
   nothing requires it here. A title attribute (GroupedRows.js) covers the
   rare case a value still overflows its line. */
.pk-grp-value{font-size:13.5px;font-weight:700;color:var(--c-ink);
  white-space:normal;word-break:break-word;
  font-variant-numeric:tabular-nums;}

/* ---- EmptyFieldsPrompt ---- */
.pk-empty{display:flex;align-items:center;gap:12px;
  padding-block:14px;padding-inline:16px;}
.pk-empty-icon{flex:none;display:inline-flex;align-items:center;
  justify-content:center;width:28px;height:28px;border-radius:50%;
  background:color-mix(in srgb,var(--c-ink) 6%,transparent);color:var(--c-muted);}
.pk-empty-text{flex:1 1 auto;min-width:0;font-size:12.5px;font-weight:600;
  color:var(--c-textLight);line-height:1.45;}
.pk-empty-add{flex:none;min-height:44px;padding-inline:10px;border:none;
  background:transparent;font-family:inherit;font-size:12px;font-weight:800;
  letter-spacing:.6px;text-transform:uppercase;color:var(--c-green);
  cursor:pointer;}

@media (prefers-reduced-motion: reduce){
  .pk-grp-tappable{transition:none;}
}

/* ---- ProfileHero ---- */
.pk-prof{padding:16px;}
.pk-prof-top{display:flex;align-items:center;gap:14px;}
.pk-prof-avatar{flex:none;display:inline-flex;align-items:center;
  justify-content:center;width:60px;height:60px;border-radius:50%;
  color:var(--c-white);font-size:22px;font-weight:800;
  box-shadow:0 0 0 2.5px var(--c-surface);}
.pk-prof-id{flex:1 1 auto;min-width:0;}
.pk-prof-name{font-size:20px;font-weight:800;color:var(--c-ink);
  letter-spacing:-.3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.pk-prof-sub{font-size:13px;font-weight:600;color:var(--c-textLight);
  margin-block-start:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.pk-prof-pill{margin-block-start:8px;}
.pk-prof-actions{display:flex;gap:10px;margin-block-start:16px;}
.pk-prof-action{flex:1 1 0;min-height:44px;display:inline-flex;
  align-items:center;justify-content:center;gap:6px;border-radius:999px;
  font-family:inherit;font-size:12.5px;font-weight:700;color:var(--c-ink);
  cursor:pointer;
  background:color-mix(in srgb,var(--c-surface) 44%,transparent);
  border:1px solid color-mix(in srgb,var(--c-surface) 55%,transparent);
  transition:transform .12s ease;}
.pk-prof-action:active{transform:scale(.96);}
.pk-prof-action-icon{display:inline-flex;color:var(--c-green);}

@media (prefers-reduced-motion: reduce){
  .pk-prof-action,.pk-prof-action:active{transition:none;transform:none;}
}

/* ---- FilterButton ---- */
.pk-fb{display:inline-flex;align-items:center;gap:6px;min-height:44px;
  padding-inline:14px;border-radius:999px;cursor:pointer;font-family:inherit;
  font-size:13px;font-weight:700;color:var(--c-ink);white-space:nowrap;
  background:color-mix(in srgb,var(--c-surface) 44%,transparent);
  border:1px solid color-mix(in srgb,var(--c-surface) 55%,transparent);
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);
  transition:transform .12s ease;}
.pk-fb:active{transform:scale(.96);}
.pk-fb-badge{display:inline-flex;align-items:center;justify-content:center;
  min-width:18px;height:18px;border-radius:999px;padding-inline:5px;
  background:var(--c-green);color:var(--c-white);
  font-size:10.5px;font-weight:800;}

/* ---- FilterSheet ---- */
.pk-fs-backdrop{position:fixed;inset:0;z-index:1000;display:flex;
  align-items:flex-end;background:color-mix(in srgb,var(--c-ink) 45%,transparent);}
.pk-fs-sheet{width:100%;max-width:560px;margin-inline:auto;box-sizing:border-box;
  border-radius:24px 24px 0 0;
  padding-block:20px calc(20px + var(--safe-bottom,0px));padding-inline:16px;
  max-height:80vh;overflow-y:auto;}
/* 3C (spec §12a, verified defect): the head used to scroll away with the
   rest of .pk-fs-sheet's content — several Plan 4 screens (Reservations,
   Products) mount three filter groups with long option lists, so the
   title and close button disappeared exactly when the list was longest.
   Sticky within the sheet's own scroll container keeps both reachable.
   The near-opaque background (plus blur, matching the kit's other glass
   surfaces) is required, not decorative — without it the chips scrolling
   underneath would show through and overlap the title/buttons. */
.pk-fs-head{position:sticky;inset-block-start:0;z-index:2;
  display:flex;align-items:center;gap:10px;margin-block-end:8px;
  padding-block-end:6px;
  background:color-mix(in srgb,var(--c-surface) 92%,transparent);
  -webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);}
.pk-fs-title{flex:1 1 auto;font-size:18px;font-weight:800;color:var(--c-ink);
  letter-spacing:-.3px;}
.pk-fs-clear{min-height:44px;padding-inline:8px;border:none;background:transparent;
  font-family:inherit;font-size:12.5px;font-weight:700;color:var(--c-green);
  cursor:pointer;}
.pk-fs-close{flex:none;display:inline-flex;align-items:center;
  justify-content:center;width:44px;height:44px;border-radius:50%;border:none;
  background:color-mix(in srgb,var(--c-ink) 6%,transparent);
  color:var(--c-muted);cursor:pointer;}
.pk-fs-group{margin-block-start:14px;}
.pk-fs-chips{display:flex;flex-wrap:wrap;gap:8px;margin-block-start:8px;}
.pk-fs-chip{min-height:44px;padding-inline:14px;border-radius:999px;
  cursor:pointer;font-family:inherit;font-size:13px;font-weight:600;
  color:var(--c-textLight);white-space:nowrap;
  background:color-mix(in srgb,var(--c-surface) 38%,transparent);
  border:1px solid color-mix(in srgb,var(--c-surface) 55%,transparent);
  transition:transform .12s ease;}
.pk-fs-chip:active{transform:scale(.96);}
.pk-fs-chip-active{background:var(--c-green);border-color:var(--c-green);
  color:var(--c-white);font-weight:700;}

@media (prefers-reduced-motion: reduce){
  .pk-fb,.pk-fb:active,.pk-fs-chip,.pk-fs-chip:active{transition:none;transform:none;}
}

@media (prefers-reduced-motion: reduce){
  .pk-row,.pk-row-tappable:active{transition:none;transform:none;}
}

/* ---- moved from operations/kit.js (Plan 4 Task 1) ----------------- */
/* SegmentedControl, SearchInput, StatusBadge, ConfirmSheet are cross-  */
/* cutting (spec §6); FilterRow and OpSummaryBar stayed in operations/  */
/* kit.js. Class names (.opk-*) kept byte-identical on the move — do   */
/* NOT rename to .pk-* here; 19 files depend on these exact strings.   */
/* .opk-num is duplicated deliberately: operations/kit.js keeps its    */
/* own copy for OpSummaryBar, which stayed behind.                     */
.opk-num{font-variant-numeric:tabular-nums;}

/* ---- ConfirmSheet ---- */
/* Centred modal, not a bottom sheet (user decision 2026-08-24). Applies to
   every ConfirmSheet in the app — 18 call sites across 14 screens — so the
   confirmation pattern stays consistent everywhere. Safe-area padding is kept
   on all sides so the dialog can never sit under the home indicator or the
   notch on a short screen. */
.opk-backdrop{position:fixed;inset:0;z-index:1000;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:center;justify-content:center;
  padding:calc(20px + var(--safe-top, 0px)) 20px
          calc(20px + var(--safe-bottom, 0px));
  box-sizing:border-box;overflow-y:auto;}
.opk-sheet{width:100%;max-width:440px;margin:auto;
  padding:24px 20px;
  box-sizing:border-box;border-radius:24px;}
.opk-sheet-title{font-size:18px;font-weight:800;letter-spacing:-.3px;
  color:var(--c-ink);margin-bottom:8px;}
.opk-sheet-msg{font-size:14px;font-weight:500;color:var(--c-textLight);
  line-height:1.55;margin-bottom:16px;}
.opk-sheet-hint{font-size:12px;font-weight:600;color:var(--c-textLight);
  margin-bottom:6px;letter-spacing:.2px;}
.opk-sheet-input{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  margin-bottom:14px;outline:none;}
.opk-sheet-input:focus{border-color:var(--c-green);}
.opk-sheet-actions{display:flex;gap:10px;margin-top:4px;}
.opk-sheet-cancel{flex:1;min-height:44px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 50%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;
  transition:transform .15s ease,background .15s ease;}
.opk-sheet-cancel:active{transform:scale(.97);}
.opk-confirm-btn{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  transition:transform .15s ease,opacity .15s ease;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;}
.opk-confirm-btn:active{transform:scale(.97);}
.opk-confirm-btn:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.opk-confirm-danger{background:var(--c-red);}
.opk-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);
  animation:opkSpin .75s linear infinite;}
@keyframes opkSpin{to{transform:rotate(360deg);}}

/* ---- SegmentedControl ---- */
.opk-seg{display:flex;gap:4px;padding:4px;border-radius:18px;
  background:color-mix(in srgb,var(--c-surface) 40%,transparent);
  -webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);
  border:1px solid color-mix(in srgb,var(--c-surface) 50%,transparent);}
.opk-seg-opt{flex:1;min-height:44px;border:none;border-radius:14px;
  background:transparent;font-family:inherit;font-size:13px;font-weight:600;
  color:var(--c-textLight);cursor:pointer;
  transition:background .18s ease,color .18s ease,transform .12s ease;}
.opk-seg-opt:active{transform:scale(.97);}
.opk-seg-active{background:var(--c-green);color:var(--c-white);font-weight:700;}

/* ---- StatusBadge ---- */
.opk-badge{display:inline-flex;align-items:center;padding:3px 9px;
  border-radius:999px;font-size:10px;font-weight:700;
  text-transform:uppercase;letter-spacing:.6px;white-space:nowrap;}
.opk-badge-green{background:color-mix(in srgb,var(--c-success) 12%,transparent);
  color:var(--c-success);}
.opk-badge-red{background:color-mix(in srgb,var(--c-red) 11%,transparent);
  color:var(--c-red);}
.opk-badge-amber{background:color-mix(in srgb,var(--c-amber) 13%,transparent);
  color:var(--c-amber);}
.opk-badge-muted{background:color-mix(in srgb,var(--c-ink) 7%,transparent);
  color:var(--c-muted);}
.opk-badge-blue{background:color-mix(in srgb,var(--c-blue) 11%,transparent);
  color:var(--c-blue);}

/* ---- SearchInput ---- */
.opk-search{display:flex;align-items:center;gap:8px;
  min-height:44px;padding:0 14px;border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 44%,transparent);
  -webkit-backdrop-filter:blur(14px);backdrop-filter:blur(14px);
  border:1px solid color-mix(in srgb,var(--c-surface) 55%,transparent);}
.opk-search-input{flex:1;min-height:44px;border:none;background:transparent;
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;}
.opk-search-input::placeholder{color:var(--c-mutedLight);}
.opk-search-icon{flex:none;color:var(--c-muted);}
.opk-search-clear{flex:none;display:inline-flex;align-items:center;
  justify-content:center;min-width:28px;min-height:28px;border:none;
  border-radius:50%;background:color-mix(in srgb,var(--c-ink) 8%,transparent);
  color:var(--c-muted);cursor:pointer;padding:0;
  transition:background .15s ease;}
.opk-search-clear:active{background:color-mix(in srgb,var(--c-ink) 14%,transparent);}

/* ---- slide-up animation (ConfirmSheet) ---- */
/* Was a slide-up from the bottom edge; a centred dialog reads better rising
   slightly with a scale, the conventional modal entrance. */
@keyframes opkSlideUp{
  from{transform:translateY(12px) scale(.97);opacity:0;}
  to{transform:translateY(0) scale(1);opacity:1;}
}
.opk-sheet-enter{animation:opkSlideUp 200ms ease-out both;}
@keyframes opkFadeIn{from{opacity:0;}to{opacity:1;}}
.opk-backdrop-enter{animation:opkFadeIn 200ms ease both;}

@media (prefers-reduced-motion: reduce){
  .opk-sheet-enter,.opk-backdrop-enter{animation:none;}
  .opk-seg-opt,.opk-seg-opt:active,
  .opk-search-clear,.opk-search-clear:active,
  .opk-confirm-btn,.opk-confirm-btn:active,
  .opk-sheet-cancel,.opk-sheet-cancel:active{transition:none;transform:none;}
  .opk-spinner{animation-duration:1.5s;}
}

/* ---- PullToRefresh ---- */
/* Root is deliberately a plain block, NOT a scroll container: screens mount
   inside a host-owned scroller (main.psh-main / .pnav-body) and the gesture
   reads scroll position from that ancestor (see PullToRefresh.js). Giving
   this an overflow-y of its own would create a second, dead scroll box whose
   scrollTop is always 0 and defeat the mid-list arm guard. The indicator is a
   real block above the content, not an absolutely-positioned overlay — its
   height grows with the pull and pushes the content down in normal flow,
   which is RTL-neutral by construction (no inline-axis offsets involved). */
.pk-ptr{display:block;}
.pk-ptr-indicator{overflow:hidden;display:flex;align-items:center;
  justify-content:center;height:0;transition:height .22s cubic-bezier(.2,.8,.2,1);}
/* No transition while the finger is down — height must track the touch 1:1.
   The transition is only for the release spring (trigger or snap-back). */
.pk-ptr-dragging .pk-ptr-indicator{transition:none;}
.pk-ptr-spinner{width:20px;height:20px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-green) 25%,transparent);
  border-top-color:var(--c-green);animation:pkPtrSpin .7s linear infinite;}
@keyframes pkPtrSpin{to{transform:rotate(360deg);}}

@media (prefers-reduced-motion: reduce){
  .pk-ptr-indicator{transition:none;}
  .pk-ptr-spinner{animation-duration:1.5s;}
}
`;

// DOM tag is the single source of truth for injection — no module-level boolean.
// Module cache can outlive the DOM tag (e.g. jest.resetModules clears cache but not
// stale references), so a boolean guard can desynchronise. This intentionally differs
// from LiquidGlass.js, which uses a boolean because it has no test-driven cleanup.
export function ensureKitCss() {
    if (typeof document === 'undefined') return;
    if (document.getElementById('pk-css')) return;
    const tag = document.createElement('style');
    tag.id = 'pk-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
}

export default ensureKitCss;
